import React from "react";

import { IImageMentionItem } from "../../@types/Note";
import { createImageReferenceHTML } from "../../utils/imageReferenceHandler";

/**
 * Configuration for Quill Mention module to support image references
 * Triggered by typing @image or #image
 */
export const createImageMentionConfig = (
  getAvailableImages: () => IImageMentionItem[],
  quillInstanceRef: React.MutableRefObject<any>,
  onInsert?: (imageKey: string, displayOrder: number) => void
) => {
  return {
    // Characters that trigger the mention dropdown. We reserve `@` for HCP
    // tagging (see noteMentionConfig) and use `#` for image references so the
    // two mention types can coexist without conflicting on the same trigger.
    mentionDenotationChars: ["#"],

    // Source function - provides the list of images to show
    source: async (
      searchTerm: string,
      renderList: (items: IImageMentionItem[], searchTerm: string) => void
    ) => {
      // Get current images from the function (always up-to-date)
      const availableImages = getAvailableImages();

      // Filter images based on search term
      const filteredImages = availableImages.filter((item) => {
        const lowerSearchTerm = searchTerm.toLowerCase();
        return (
          item.value.toLowerCase().includes(lowerSearchTerm) ||
          `image${item.displayOrder}`.includes(lowerSearchTerm) ||
          item.displayOrder.toString().includes(searchTerm)
        );
      });

      renderList(filteredImages, searchTerm);
    },

    // Render function - how each item appears in the dropdown
    renderItem: (item: IImageMentionItem) => {
      const div = document.createElement("div");
      div.className = "mention-item image-mention-item";

      // Create thumbnail preview if URL is available
      if (item.preSignedUrl) {
        const img = document.createElement("img");
        img.src = item.preSignedUrl;
        img.className = "mention-thumbnail";
        img.alt = item.value;
        div.appendChild(img);
      } else {
        // Placeholder icon if no preview available
        const icon = document.createElement("span");
        icon.className = "mention-icon";
        icon.innerHTML = "📷";
        div.appendChild(icon);
      }

      // Display text
      const text = document.createElement("span");
      text.className = "mention-text";
      text.textContent = item.value;
      div.appendChild(text);

      return div;
    },

    // What gets inserted when user selects an item
    onSelect: (item: IImageMentionItem) => {
      // Call the callback if provided
      if (onInsert) {
        onInsert(item.id, item.displayOrder);
      }

      // Get the quill instance
      const quill = quillInstanceRef.current;
      if (!quill) {
        console.error("Quill instance not available");
        return;
      }

      // Create the reference HTML
      const referenceHTML = createImageReferenceHTML(
        item.id,
        item.displayOrder
      );

      // Get the mention module to find where the mention started
      const mentionModule = quill.getModule("mention");
      if (!mentionModule || !mentionModule.mentionCharPos) {
        console.error("Mention module not properly initialized");
        return;
      }

      // Get the position where the mention started (@ or # position)
      const mentionStartPos = mentionModule.mentionCharPos;
      const currentPos = quill.getSelection()?.index || 0;

      // Delete the mention text (from @ to cursor)
      const mentionLength = currentPos - mentionStartPos;
      quill.deleteText(mentionStartPos, mentionLength);

      // Insert our custom HTML at the mention position
      quill.clipboard.dangerouslyPasteHTML(
        mentionStartPos,
        `${referenceHTML}&nbsp;`,
        "user"
      );

      // Set cursor after the inserted reference
      quill.setSelection(mentionStartPos + 1, 0);

      // Close the mention dropdown
      if (mentionModule.hideMentionList) {
        mentionModule.hideMentionList();
      }
    },

    // Allow mentions to appear anywhere in the text
    allowedChars: /^[a-zA-Z0-9_\s]*$/,

    // Minimum characters before showing dropdown (0 = show immediately)
    minChars: 0,

    // Show dropdown on trigger character only (not after any character)
    showDenotationChar: true,

    // Position of dropdown
    positioningStrategy: "absolute" as const,

    // Dropdown styles will be handled by CSS
    dataAttributes: ["id", "value", "displayOrder", "image-ref"],

    // List container class for styling
    listItemClass: "ql-mention-list-item",
    mentionContainerClass: "ql-mention-list-container",

    // Mention character in final output (empty string)
    mentionChar: "",

    // How to identify mentions in the content
    isolateCharacter: false,

    // Fix iOS selection issues
    fixMentionsToQuill: true,

    // Default sorting (by display order)
    defaultMenuOrientation: "bottom" as const,

    // Offset from cursor
    offsetTop: 2,
    offsetLeft: 0,

    // Spellcheck behavior
    spellChecker: false,
  };
};

/**
 * Registers custom Quill format for image references
 * This ensures references are treated as non-editable blocks
 */
export const registerImageReferenceBlot = (Quill: any) => {
  const Inline = Quill.import("blots/inline");

  class ImageReferenceBlot extends Inline {
    static blotName = "imageReference";
    static tagName = "span";
    static className = "image-ref";

    static create(data: { imageKey: string; displayOrder: number }) {
      const node = super.create();
      node.setAttribute("data-image-key", data.imageKey);
      node.setAttribute("data-image-order", data.displayOrder.toString());
      node.setAttribute("contenteditable", "false");
      node.textContent = `Image ${data.displayOrder}`;
      return node;
    }

    static formats(node: HTMLElement) {
      return {
        imageKey: node.getAttribute("data-image-key"),
        displayOrder: parseInt(
          node.getAttribute("data-image-order") || "0",
          10
        ),
      };
    }

    format(name: string, value: any) {
      if (name === "imageReference" && value) {
        this.domNode.setAttribute("data-image-key", value.imageKey);
        this.domNode.setAttribute(
          "data-image-order",
          value.displayOrder.toString()
        );
      } else {
        super.format(name, value);
      }
    }
  }

  Quill.register(ImageReferenceBlot);
};
